import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALLOWED_TYPES, putToGcs, useCreateUpload, validateFile } from '../../api/uploads.js';
import { PageIdentity, tenantSlug } from '../../shell/PageIdentity.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

interface Props {
  tenantId: string;
  tenantName: string;
}

type Phase = 'idle' | 'granting' | 'uploading' | 'done' | 'error';

export function UploadScreen({ tenantId, tenantName }: Props) {
  const navigate = useNavigate();
  const createUpload = useCreateUpload();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  function acceptFile(f: File) {
    setError(null);
    const v = validateFile(f);
    if (!v.ok) {
      setError(v.reason);
      setFile(null);
      setPhase('error');
      return;
    }
    setFile(f);
    setPhase('idle');
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) acceptFile(f);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
    e.target.value = '';
  }

  async function startUpload() {
    if (!file) return;
    setError(null);
    setPhase('granting');
    try {
      const grant = await createUpload.mutateAsync({ tenantId, file });
      setPhase('uploading');
      setProgress({ loaded: 0, total: file.size });
      abortRef.current = new AbortController();
      await putToGcs({
        uploadUrl: grant.uploadUrl,
        file,
        signal: abortRef.current.signal,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      });
      setPhase('done');
      navigate(`/t/${tenantId}/invoices/${encodeURIComponent(grant.primaryKey)}`);
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        setError('Upload annulé.');
      } else {
        setError("Échec de l'envoi. Réessayez.");
      }
      setPhase('error');
    }
  }

  function cancelUpload() {
    abortRef.current?.abort();
  }

  function reset() {
    setFile(null);
    setPhase('idle');
    setError(null);
    setProgress({ loaded: 0, total: 0 });
  }

  const isUploading = phase === 'granting' || phase === 'uploading';
  const pct = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
    <PageWrapper>
      <section className="invoices" aria-label="Importer une facture">
        <PageIdentity
          testId="upload"
          eyebrow={`${tenantSlug(tenantName)} · IMPORT MANUEL`}
          title="Importer une facture"
          subtitle="Téléversez une facture reçue hors PDP (Factur-X / UBL / CII / PDF non structuré). Les formats structurés sont auto-remplis ; le PDF nu reste à compléter manuellement."
        />

        <div
          className={`upload-dropzone${dragOver ? ' upload-dropzone--hover' : ''}${file ? ' upload-dropzone--has-file' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !isUploading && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          data-testid="upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            onChange={onPick}
            style={{ display: 'none' }}
            data-testid="upload-input"
          />

          {file ? (
            <div className="upload-file">
              <div className="upload-file__name">{file.name}</div>
              <div className="upload-file__meta muted">
                {(file.size / (1024 * 1024)).toFixed(2)} Mo · {file.type}
              </div>
            </div>
          ) : (
            <div className="upload-prompt">
              <div className="upload-prompt__icon" aria-hidden="true">
                ↓
              </div>
              <div className="upload-prompt__title display">Glissez un fichier ici</div>
              <div className="upload-prompt__sub muted">
                ou cliquez pour parcourir · PDF / PNG / JPG / ZIP · 25 Mo max
              </div>
            </div>
          )}
        </div>

        {isUploading && file ? (
          <div className="upload-progress" data-testid="upload-progress">
            <div className="upload-progress__bar">
              <div
                className="upload-progress__fill"
                style={{ width: `${pct}%` }}
                data-testid="upload-progress-fill"
              />
            </div>
            <div className="upload-progress__meta muted">
              {phase === 'granting'
                ? 'Préparation…'
                : `${pct}% · ${(progress.loaded / (1024 * 1024)).toFixed(1)} / ${(progress.total / (1024 * 1024)).toFixed(1)} Mo`}
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="invoices__error" role="alert" data-testid="upload-error">
            <span>{error}</span>
            <button type="button" className="btn-ghost" onClick={reset}>
              Choisir un autre fichier
            </button>
          </div>
        ) : null}

        <div className="upload-actions">
          {isUploading ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={cancelUpload}
              data-testid="upload-cancel"
            >
              Annuler
            </button>
          ) : (
            <>
              {file ? (
                <button type="button" className="btn-ghost" onClick={reset}>
                  Choisir un autre fichier
                </button>
              ) : null}
              <button
                type="button"
                className="btn-primary"
                onClick={startUpload}
                disabled={!file || isUploading}
                data-testid="upload-submit"
              >
                {phase === 'done' ? 'Envoyé.' : 'Envoyer la facture'}
              </button>
            </>
          )}
        </div>

        <p className="muted" style={{ fontSize: 12 }}>
          Formats acceptés : PDF, PNG, JPG, ZIP — jusqu&apos;à 25 Mo. La facture passera en état{' '}
          <strong>PROCESSING</strong> puis <strong>AVAILABLE</strong> en quelques secondes après
          l&apos;envoi.
        </p>
      </section>
    </PageWrapper>
  );
}
